import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardCopy, Printer } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { fetchCadeDecisions, fetchCadePautas, fetchCadeSessions } from '@/services/cade';
import type { CadeDecisionHit, CadePautaSession, CadeSession } from '@/services/types';
import { fetchBulletinDraft, saveBulletinDraft, type BulletinDraft } from '@/services/workspace';

const DRAFT_KEY = 'cade-bulletin-draft';

interface Draft {
  comment: string;
  signature: string;
}

function readDraft(): Draft {
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    const parsed = raw ? (JSON.parse(raw) as Partial<Draft>) : {};
    return { comment: parsed.comment ?? '', signature: parsed.signature ?? '' };
  } catch {
    return { comment: '', signature: '' };
  }
}

function writeDraft(value: Draft): void {
  try {
    window.localStorage.setItem(DRAFT_KEY, JSON.stringify(value));
  } catch {
    // Storage can be blocked or full; the draft then lives for this visit only.
  }
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface Words {
  t: Translate;
  day: (iso: string) => string;
  outcome: (code: string) => string;
}

function sessionDate(session: CadeSession, words: Words): string {
  if (session.session_date) return words.day(session.session_date);
  return session.session_date_raw ?? '—';
}

function judgedLine(hit: CadeDecisionHit, words: Words): string {
  const results = hit.outcome_codes.map(words.outcome).join(', ') || '—';
  return `${hit.case_number} (${hit.relator ?? '—'}): ${results}`;
}

function pautaLine(item: CadePautaSession['items'][number]): string {
  return `${item.case_number ?? '—'} (${item.case_type ?? '—'}; ${item.relator ?? '—'})`;
}

/** The bulletin as plain text, for pasting into an e-mail: the same lines the page shows. */
function bulletinText(
  newest: CadeSession | undefined,
  hits: CadeDecisionHit[],
  upcoming: CadePautaSession | undefined,
  draft: Draft,
  words: Words,
): string {
  const lines: string[] = [words.t('cade:bulletin.title'), ''];
  if (newest) {
    lines.push(
      words.t('cade:bulletin.judgedTitle', {
        number: newest.session_number ?? '?',
        date: sessionDate(newest, words),
      }),
    );
    lines.push(...hits.map((hit) => `- ${judgedLine(hit, words)}`), '');
  }
  if (upcoming) {
    lines.push(
      words.t('cade:bulletin.pautaTitle', {
        number: upcoming.number ?? '?',
        date: words.day(upcoming.date),
      }),
    );
    lines.push(...upcoming.items.map((item) => `- ${pautaLine(item)}`), '');
  }
  if (draft.comment.trim()) lines.push(draft.comment.trim(), '');
  if (draft.signature.trim()) lines.push(draft.signature.trim());
  return lines.join('\n');
}

/**
 * A fortnightly bulletin DRAFT: what the Tribunal's newest session judged, what the newest
 * pauta lists, and a box for the signed technical comment.
 *
 * Everything above the comment is read from the archive and says so: the judged items are the
 * newest session the archive holds, and a pauta lists what was announced, not what was decided.
 * The page is built to be printed or copied as text (the draft and signature print with it;
 * the editing controls do not), because the bulletin is an editorial product the platform
 * prepares and a person signs and sends.
 */
function useBulletinData() {
  const sessions = useQuery({
    queryKey: ['cade', 'bulletin', 'session'],
    queryFn: () => fetchCadeSessions({ limit: 1 }),
  });
  const newest: CadeSession | undefined = sessions.data?.items[0];
  const judged = useQuery({
    queryKey: ['cade', 'bulletin', 'judged', newest?.session_key],
    queryFn: () => fetchCadeDecisions({ session: newest?.session_key, limit: 100 }),
    enabled: Boolean(newest),
  });
  const pautas = useQuery({
    queryKey: ['cade', 'bulletin', 'pauta'],
    queryFn: () => fetchCadePautas(1),
  });
  return {
    asOf: sessions.data?.as_of,
    newest,
    hits: judged.data?.items ?? [],
    upcoming: pautas.data?.pautas.value?.sessions[0],
    failure: [sessions, judged, pautas].find((q) => q.error)?.error,
    loading: sessions.isPending || pautas.isPending,
  };
}

export function CadeBulletinPage() {
  const { t, i18n } = useTranslation(['cade']);
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(readDraft);
  const { asOf, newest, hits, upcoming, failure, loading } = useBulletinData();
  // An account in a firm writes the firm's one draft; any other keeps it in this browser.
  const shared = useQuery({
    queryKey: ['workspace', 'bulletin-draft', 'cade'],
    queryFn: () => fetchBulletinDraft('cade'),
    retry: false,
  });
  const firmDraft = shared.data?.shared ? shared.data : null;
  const [loadedFrom, setLoadedFrom] = useState<string | null>(null);
  useEffect(() => {
    // Take the firm's draft once, when it arrives (and again after this account saves it).
    const stamp = firmDraft ? (firmDraft.updated_at ?? 'empty') : null;
    if (firmDraft && stamp !== loadedFrom) {
      setDraft({ comment: firmDraft.comment, signature: firmDraft.signature });
      setLoadedFrom(stamp);
    }
  }, [firmDraft, loadedFrom]);
  const save = useMutation({
    mutationFn: () => saveBulletinDraft('cade', draft),
    onSuccess: (saved) => {
      queryClient.setQueryData(['workspace', 'bulletin-draft', 'cade'], saved);
      toast.success(t('cade:bulletin.firm.saved'));
    },
    onError: () => toast.error(t('cade:bulletin.firm.saveFailed')),
  });

  const words: Words = {
    t: t as Translate,
    day: (iso) =>
      new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long', timeZone: 'UTC' }).format(
        new Date(`${iso}T00:00:00Z`),
      ),
    outcome: (code) => t(`cade:overview.outcomes.${code}`, { defaultValue: code }),
  };

  const update = (next: Partial<Draft>) => {
    const value = { ...draft, ...next };
    setDraft(value);
    if (!firmDraft) writeDraft(value);
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(bulletinText(newest, hits, upcoming, draft, words));
      toast.success(t('cade:bulletin.copied'));
    } catch {
      toast.error(t('cade:bulletin.copyFailed'));
    }
  };

  return (
    <div className="space-y-5 p-6" data-bulletin>
      <Header onCopy={() => void copy()} />
      {asOf ? <AsOfStrip asOf={asOf} /> : null}
      {failure ? <ErrorMessage message={failure.message} /> : null}
      {loading ? <SkeletonLoader rows={4} /> : null}
      {newest ? <JudgedSection session={newest} hits={hits} words={words} /> : null}
      {upcoming ? <PautaSection session={upcoming} words={words} /> : null}
      <CommentSection
        draft={draft}
        onChange={update}
        firm={firmDraft}
        dirty={
          firmDraft !== null &&
          (draft.comment !== firmDraft.comment || draft.signature !== firmDraft.signature)
        }
        saving={save.isPending}
        onSave={() => save.mutate()}
      />
    </div>
  );
}

function Header({ onCopy }: { onCopy: () => void }) {
  const { t } = useTranslation(['cade']);
  return (
    <header className="flex flex-wrap items-start justify-between gap-2">
      <div className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cade:bulletin.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cade:bulletin.subtitle')}</p>
      </div>
      <div className="flex gap-2" data-print-hide>
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm text-foreground"
        >
          <Printer className="h-3.5 w-3.5" aria-hidden />
          {t('cade:bulletin.print')}
        </button>
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm text-foreground"
        >
          <ClipboardCopy className="h-3.5 w-3.5" aria-hidden />
          {t('cade:bulletin.copy')}
        </button>
      </div>
    </header>
  );
}

function JudgedSection({
  session,
  hits,
  words,
}: {
  session: CadeSession;
  hits: CadeDecisionHit[];
  words: Words;
}) {
  const { t } = useTranslation(['cade']);
  return (
    <section className="space-y-2" data-bulletin-judged>
      <h2 className="text-sm font-semibold text-foreground">
        {t('cade:bulletin.judgedTitle', {
          number: session.session_number ?? '?',
          date: sessionDate(session, words),
        })}
      </h2>
      <p className="text-xs text-muted-foreground">
        {t('cade:bulletin.judgedNote', { count: session.decisions })}
      </p>
      <ul className="divide-y rounded-md border text-sm">
        {hits.map((hit) => (
          <li key={`${hit.case_key}-${hit.item_number}`} className="flex flex-wrap gap-x-3 p-2">
            <span className="tabular-nums text-foreground">{hit.case_number}</span>
            <span className="text-muted-foreground">{hit.relator ?? '—'}</span>
            <span className="text-foreground">
              {hit.outcome_codes.map(words.outcome).join(', ') || '—'}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function PautaSection({ session, words }: { session: CadePautaSession; words: Words }) {
  const { t } = useTranslation(['cade']);
  return (
    <section className="space-y-2" data-bulletin-pauta>
      <h2 className="text-sm font-semibold text-foreground">
        {t('cade:bulletin.pautaTitle', {
          number: session.number ?? '?',
          date: words.day(session.date),
        })}
      </h2>
      <p className="text-xs text-muted-foreground">{t('cade:bulletin.pautaNote')}</p>
      <ul className="divide-y rounded-md border text-sm">
        {session.items.map((item, index) => (
          <li key={`${item.case_number}-${index}`} className="flex flex-wrap gap-x-3 p-2">
            <span className="tabular-nums text-foreground">{item.case_number ?? '—'}</span>
            <span className="text-muted-foreground">{item.case_type ?? '—'}</span>
            <span className="text-muted-foreground">{item.relator ?? '—'}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function CommentSection({
  draft,
  onChange,
  firm,
  dirty,
  saving,
  onSave,
}: {
  draft: Draft;
  onChange: (next: Partial<Draft>) => void;
  /** The firm's shared draft, or null when this account belongs to no firm. */
  firm: BulletinDraft | null;
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
}) {
  const { t, i18n } = useTranslation(['cade']);
  return (
    <section className="space-y-2" data-bulletin-comment>
      <h2 className="text-sm font-semibold text-foreground">{t('cade:bulletin.commentTitle')}</h2>
      <label className="sr-only" htmlFor="bulletin-comment">
        {t('cade:bulletin.commentTitle')}
      </label>
      <textarea
        id="bulletin-comment"
        value={draft.comment}
        onChange={(event) => onChange({ comment: event.target.value })}
        rows={8}
        placeholder={t('cade:bulletin.commentPlaceholder')}
        className="w-full rounded-md border bg-background p-3 text-sm text-foreground"
      />
      <input
        value={draft.signature}
        onChange={(event) => onChange({ signature: event.target.value })}
        placeholder={t('cade:bulletin.signaturePlaceholder')}
        aria-label={t('cade:bulletin.signaturePlaceholder')}
        data-print-hide
        className="w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
      />
      {/* The editing controls do not print; this is what the page prints in their place. */}
      <div className="hidden whitespace-pre-wrap text-sm text-foreground print:block">
        {draft.comment}
        {draft.signature ? `\n\n${draft.signature}` : ''}
      </div>
      {firm ? (
        <div className="flex flex-wrap items-center gap-3" data-print-hide data-bulletin-firm>
          <button
            type="button"
            onClick={onSave}
            disabled={saving || !dirty}
            className="rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-50"
          >
            {t('cade:bulletin.firm.save', { firm: firm.firm })}
          </button>
          <span className="text-xs text-muted-foreground">
            {firm.updated_by && firm.updated_at
              ? t('cade:bulletin.firm.lastSaved', {
                  name: firm.updated_by,
                  date: new Intl.DateTimeFormat(i18n.language, {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  }).format(new Date(firm.updated_at)),
                })
              : t('cade:bulletin.firm.neverSaved')}
          </span>
        </div>
      ) : null}
      <p className="text-xs text-muted-foreground" data-print-hide>
        {firm ? t('cade:bulletin.firm.note', { firm: firm.firm }) : t('cade:bulletin.draftNote')}
      </p>
    </section>
  );
}
